/**
 * Systematic Reviews Contract — Typed API contract for SR screening & funnel.
 *
 * PR 8 of VIZ-RFC-0012: screening workspace, funnel visualization,
 * exclusion analytics, conflict resolution, screening stats.
 *
 * BACKEND ALIGNMENT:
 * - All types derived from auto-generated Zod schemas (hey-api/openapi-ts)
 * - Follows the same adapter → service → hook pattern as other domains
 */

import { z } from 'zod';
import type { PaginatedResponse, PaginationParams } from './shared';

// Re-export generated schemas
import {
  zSrStatus as GeneratedSRStatus,
  zSrMemberRole as GeneratedSRMemberRole,
  zSrDecision as GeneratedSRDecision,
  zSrFinalDecision as GeneratedSRFinalDecision,
  zSrConflictStatus as GeneratedSRConflictStatus,
  zSrResolutionType as GeneratedSRResolutionType,
  zSrScreeningStage as GeneratedSRScreeningStage,
  zSrCriterionType as GeneratedSRCriterionType,
  zSrRecordSource as GeneratedSRRecordSource,
  zSrRecordStatus as GeneratedSRRecordStatus,
  zSrQuestionFramework as GeneratedSRQuestionFramework,
  zSrProjectRead as GeneratedSRProjectRead,
  zSrProjectListItem as GeneratedSRProjectListItem,
  zSrProjectCreate as GeneratedSRProjectCreate,
  zSrProjectUpdate as GeneratedSRProjectUpdate,
  zSrMemberAdd as GeneratedSRMemberAdd,
  zSrMemberRead as GeneratedSRMemberRead,
  zSrRecordListItem as GeneratedSRRecordListItem,
  zSrRecordRead as GeneratedSRRecordRead,
  zSrScreeningStats as GeneratedSRScreeningStats,
  zSrExclusionBreakdown as GeneratedSRExclusionBreakdown,
  zSrExclusionAnalysis as GeneratedSRExclusionAnalysis,
  zSrExclusionReasonCount as GeneratedSRExclusionReasonCount,
  zSrCriterionRead as GeneratedSRCriterionRead,
  zFunnelSummary as GeneratedFunnelSummary,
  zFunnelStageScreening as GeneratedFunnelStageScreening,
  zFunnelStageSynthesis as GeneratedFunnelStageSynthesis,
  zSrStageOutcomeRead as GeneratedSRStageOutcomeRead,
  zSrConflictRead as GeneratedSRConflictRead,
  zSrDecisionRead as GeneratedSRDecisionRead,
  zSrAdvanceStatus as GeneratedSRAdvanceStatus,
  zSrConflictResolveConsensus as GeneratedSRConflictResolveConsensus,
  // ── New: Criteria CRUD ──
  zSrCriterionCreate as GeneratedSRCriterionCreate,
  zSrCriterionUpdate as GeneratedSRCriterionUpdate,
  // ── New: Import ──
  zSrImportResult as GeneratedSRImportResult,
  // ── New: Exclusion reasons ──
  zSrExclusionReasonCreate as GeneratedSRExclusionReasonCreate,
  zSrExclusionReasonRead as GeneratedSRExclusionReasonRead,
  // ── New: Extraction ──
  zSrExtractionFieldType as GeneratedSRExtractionFieldType,
  zSrExtractionFieldCreate as GeneratedSRExtractionFieldCreate,
  zSrExtractionFieldRead as GeneratedSRExtractionFieldRead,
  zSrExtractionFieldUpdate as GeneratedSRExtractionFieldUpdate,
  zSrExtractionTemplate as GeneratedSRExtractionTemplate,
  zSrExtractionValueCreate as GeneratedSRExtractionValueCreate,
  zSrExtractionValueRead as GeneratedSRExtractionValueRead,
  zSrExtractionRecordSummary as GeneratedSRExtractionRecordSummary,
  // ── New: Risk of Bias ──
  zSrRoBassessmentCreate as GeneratedSRRoBAssessmentCreate,
  zSrRoBassessmentRead as GeneratedSRRoBAssessmentRead,
  zSrRoBassessmentUpdate as GeneratedSRRoBAssessmentUpdate,
  zSrRoBJudgmentSummary as GeneratedSRRoBJudgmentSummary,
  zSrRoBTrafficLightEntry as GeneratedSRRoBTrafficLightEntry,
  // ── New: Manuscript ──
  zSrManuscriptSectionType as GeneratedSRManuscriptSectionType,
  zSrManuscriptSectionRead as GeneratedSRManuscriptSectionRead,
  zSrManuscriptRead as GeneratedSRManuscriptRead,
  zSrManuscriptGenerateResponse as GeneratedSRManuscriptGenerateResponse,
  zSrRegenerateRequest as GeneratedSRRegenerateRequest,
  zSrRegenerateResponse as GeneratedSRRegenerateResponse,
  zSrSectionLockUpdate as GeneratedSRSectionLockUpdate,
  zSrSectionStaleness as GeneratedSRSectionStaleness,
  // ── New: PRISMA ──
  zSrPrismaCounts as GeneratedSRPrismaCounts,
  // ── New: Evidence table ──
  zSrEvidenceRow as GeneratedSREvidenceRow,
  zSrEvidenceTable as GeneratedSREvidenceTable,
  // ── New: Audit trail ──
  zSrAuditEntry as GeneratedSRAuditEntry,
  zSrAuditTrailPage as GeneratedSRAuditTrailPage,
  // ── New: Amendments ──
  zSrAmendmentCreate as GeneratedSRAmendmentCreate,
  zSrAmendmentRead as GeneratedSRAmendmentRead,
} from '@/api/generated/hey-api/zod.gen';

// ─── Enums ─────────────────────────────────────────────────────────────

export const SRStatusEnum = GeneratedSRStatus;
export type SRStatus = z.infer<typeof SRStatusEnum>;

export const SRMemberRoleEnum = GeneratedSRMemberRole;
export type SRMemberRole = z.infer<typeof SRMemberRoleEnum>;

export const SRDecisionEnum = GeneratedSRDecision;
export type SRDecision = z.infer<typeof SRDecisionEnum>;

export const SRFinalDecisionEnum = GeneratedSRFinalDecision;
export type SRFinalDecision = z.infer<typeof SRFinalDecisionEnum>;

export const SRConflictStatusEnum = GeneratedSRConflictStatus;
export type SRConflictStatus = z.infer<typeof SRConflictStatusEnum>;

export const SRResolutionTypeEnum = GeneratedSRResolutionType;
export type SRResolutionType = z.infer<typeof SRResolutionTypeEnum>;

export const SRScreeningStageEnum = GeneratedSRScreeningStage;
export type SRScreeningStage = z.infer<typeof SRScreeningStageEnum>;

export const SRCriterionTypeEnum = GeneratedSRCriterionType;
export type SRCriterionType = z.infer<typeof SRCriterionTypeEnum>;

export const SRRecordSourceEnum = GeneratedSRRecordSource;
export type SRRecordSource = z.infer<typeof SRRecordSourceEnum>;

export const SRRecordStatusEnum = GeneratedSRRecordStatus;
export type SRRecordStatus = z.infer<typeof SRRecordStatusEnum>;

export const SRQuestionFrameworkEnum = GeneratedSRQuestionFramework;
export type SRQuestionFramework = z.infer<typeof SRQuestionFrameworkEnum>;

// ─── DTOs ──────────────────────────────────────────────────────────────

export const SRProjectReadSchema = GeneratedSRProjectRead;
export type SRProjectRead = z.infer<typeof SRProjectReadSchema>;

export const SRProjectListItemSchema = GeneratedSRProjectListItem;
export type SRProjectListItem = z.infer<typeof SRProjectListItemSchema>;

export const SRProjectCreateSchema = GeneratedSRProjectCreate;
export type SRProjectCreate = z.infer<typeof SRProjectCreateSchema>;

export const SRProjectUpdateSchema = GeneratedSRProjectUpdate;
export type SRProjectUpdate = z.infer<typeof SRProjectUpdateSchema>;

export const SRMemberAddSchema = GeneratedSRMemberAdd;
export type SRMemberAdd = z.infer<typeof SRMemberAddSchema>;

export const SRMemberReadSchema = GeneratedSRMemberRead;
export type SRMemberRead = z.infer<typeof SRMemberReadSchema>;

export const SRRecordListItemSchema = GeneratedSRRecordListItem;
export type SRRecordListItem = z.infer<typeof SRRecordListItemSchema>;

export const SRRecordReadSchema = GeneratedSRRecordRead;
export type SRRecordRead = z.infer<typeof SRRecordReadSchema>;

export const SRScreeningStatsSchema = GeneratedSRScreeningStats;
export type SRScreeningStats = z.infer<typeof SRScreeningStatsSchema>;

export const SRExclusionBreakdownSchema = GeneratedSRExclusionBreakdown;
export type SRExclusionBreakdown = z.infer<typeof SRExclusionBreakdownSchema>;

export const SRExclusionAnalysisSchema = GeneratedSRExclusionAnalysis;
export type SRExclusionAnalysis = z.infer<typeof SRExclusionAnalysisSchema>;

export const SRExclusionReasonCountSchema = GeneratedSRExclusionReasonCount;
export type SRExclusionReasonCount = z.infer<typeof SRExclusionReasonCountSchema>;

export const SRCriterionReadSchema = GeneratedSRCriterionRead;
export type SRCriterionRead = z.infer<typeof SRCriterionReadSchema>;

export const FunnelSummarySchema = GeneratedFunnelSummary;
export type FunnelSummary = z.infer<typeof FunnelSummarySchema>;

export const FunnelStageScreeningSchema = GeneratedFunnelStageScreening;
export type FunnelStageScreening = z.infer<typeof FunnelStageScreeningSchema>;

export const FunnelStageSynthesisSchema = GeneratedFunnelStageSynthesis;
export type FunnelStageSynthesis = z.infer<typeof FunnelStageSynthesisSchema>;

export const SRStageOutcomeReadSchema = GeneratedSRStageOutcomeRead;
export type SRStageOutcomeRead = z.infer<typeof SRStageOutcomeReadSchema>;

export const SRConflictReadSchema = GeneratedSRConflictRead;
export type SRConflictRead = z.infer<typeof SRConflictReadSchema>;

export const SRDecisionReadSchema = GeneratedSRDecisionRead;
export type SRDecisionRead = z.infer<typeof SRDecisionReadSchema>;

export const SRAdvanceStatusSchema = GeneratedSRAdvanceStatus;
export type SRAdvanceStatus = z.infer<typeof SRAdvanceStatusSchema>;

export const SRConflictResolveConsensusSchema = GeneratedSRConflictResolveConsensus;
export type SRConflictResolveConsensus = z.infer<typeof SRConflictResolveConsensusSchema>;

// ── New: Criteria CRUD ──

export const SRCriterionCreateSchema = GeneratedSRCriterionCreate;
export type SRCriterionCreate = z.infer<typeof SRCriterionCreateSchema>;

export const SRCriterionUpdateSchema = GeneratedSRCriterionUpdate;
export type SRCriterionUpdate = z.infer<typeof SRCriterionUpdateSchema>;

// ── New: Import ──

export const SRImportResultSchema = GeneratedSRImportResult;
export type SRImportResult = z.infer<typeof SRImportResultSchema>;

// ── New: Exclusion reasons ──

export const SRExclusionReasonCreateSchema = GeneratedSRExclusionReasonCreate;
export type SRExclusionReasonCreate = z.infer<typeof SRExclusionReasonCreateSchema>;

export const SRExclusionReasonReadSchema = GeneratedSRExclusionReasonRead;
export type SRExclusionReasonRead = z.infer<typeof SRExclusionReasonReadSchema>;

// ── New: Extraction ──

export const SRExtractionFieldTypeEnum = GeneratedSRExtractionFieldType;
export type SRExtractionFieldType = z.infer<typeof SRExtractionFieldTypeEnum>;

export const SRExtractionFieldCreateSchema = GeneratedSRExtractionFieldCreate;
export type SRExtractionFieldCreate = z.infer<typeof SRExtractionFieldCreateSchema>;

export const SRExtractionFieldReadSchema = GeneratedSRExtractionFieldRead;
export type SRExtractionFieldRead = z.infer<typeof SRExtractionFieldReadSchema>;

export const SRExtractionFieldUpdateSchema = GeneratedSRExtractionFieldUpdate;
export type SRExtractionFieldUpdate = z.infer<typeof SRExtractionFieldUpdateSchema>;

export const SRExtractionValueCreateSchema = GeneratedSRExtractionValueCreate;
export type SRExtractionValueCreate = z.infer<typeof SRExtractionValueCreateSchema>;

export const SRExtractionValueReadSchema = GeneratedSRExtractionValueRead;
export type SRExtractionValueRead = z.infer<typeof SRExtractionValueReadSchema>;

export const SRExtractionRecordSummarySchema = GeneratedSRExtractionRecordSummary;
export type SRExtractionRecordSummary = z.infer<typeof SRExtractionRecordSummarySchema>;

// ── New: Risk of Bias ──

export const SRRoBAssessmentCreateSchema = GeneratedSRRoBAssessmentCreate;
export type SRRoBAssessmentCreate = z.infer<typeof SRRoBAssessmentCreateSchema>;

export const SRRoBAssessmentReadSchema = GeneratedSRRoBAssessmentRead;
export type SRRoBAssessmentRead = z.infer<typeof SRRoBAssessmentReadSchema>;

export const SRRoBAssessmentUpdateSchema = GeneratedSRRoBAssessmentUpdate;
export type SRRoBAssessmentUpdate = z.infer<typeof SRRoBAssessmentUpdateSchema>;

export const SRRoBJudgmentSummarySchema = GeneratedSRRoBJudgmentSummary;
export type SRRoBJudgmentSummary = z.infer<typeof SRRoBJudgmentSummarySchema>;

export const SRRoBTrafficLightEntrySchema = GeneratedSRRoBTrafficLightEntry;
export type SRRoBTrafficLightEntry = z.infer<typeof SRRoBTrafficLightEntrySchema>;

// ── New: Manuscript ──

export const SRManuscriptSectionTypeEnum = GeneratedSRManuscriptSectionType;
export type SRManuscriptSectionType = z.infer<typeof SRManuscriptSectionTypeEnum>;

export const SRManuscriptSectionReadSchema = GeneratedSRManuscriptSectionRead;
export type SRManuscriptSectionRead = z.infer<typeof SRManuscriptSectionReadSchema>;

export const SRManuscriptReadSchema = GeneratedSRManuscriptRead;
export type SRManuscriptRead = z.infer<typeof SRManuscriptReadSchema>;

export const SRManuscriptGenerateResponseSchema = GeneratedSRManuscriptGenerateResponse;
export type SRManuscriptGenerateResponse = z.infer<typeof SRManuscriptGenerateResponseSchema>;

export const SRRegenerateRequestSchema = GeneratedSRRegenerateRequest;
export type SRRegenerateRequest = z.infer<typeof SRRegenerateRequestSchema>;

export const SRRegenerateResponseSchema = GeneratedSRRegenerateResponse;
export type SRRegenerateResponse = z.infer<typeof SRRegenerateResponseSchema>;

export const SRSectionLockUpdateSchema = GeneratedSRSectionLockUpdate;
export type SRSectionLockUpdate = z.infer<typeof SRSectionLockUpdateSchema>;

export const SRSectionStalenessSchema = GeneratedSRSectionStaleness;
export type SRSectionStaleness = z.infer<typeof SRSectionStalenessSchema>;

// ── New: PRISMA ──

export const SRPrismaCountsSchema = GeneratedSRPrismaCounts;
export type SRPrismaCounts = z.infer<typeof SRPrismaCountsSchema>;

// ── New: Evidence Table ──

export const SREvidenceRowSchema = GeneratedSREvidenceRow;
export type SREvidenceRow = z.infer<typeof SREvidenceRowSchema>;

export const SREvidenceTableSchema = GeneratedSREvidenceTable;
export type SREvidenceTable = z.infer<typeof SREvidenceTableSchema>;

// ── New: Audit Trail ──

export const SRAuditEntrySchema = GeneratedSRAuditEntry;
export type SRAuditEntry = z.infer<typeof SRAuditEntrySchema>;

export const SRAuditTrailPageSchema = GeneratedSRAuditTrailPage;
export type SRAuditTrailPage = z.infer<typeof SRAuditTrailPageSchema>;

// ── New: Amendments ──

export const SRAmendmentCreateSchema = GeneratedSRAmendmentCreate;
export type SRAmendmentCreate = z.infer<typeof SRAmendmentCreateSchema>;

export const SRAmendmentReadSchema = GeneratedSRAmendmentRead;
export type SRAmendmentRead = z.infer<typeof SRAmendmentReadSchema>;

// ── Extraction Templates ──

export const SRExtractionTemplateSchema = GeneratedSRExtractionTemplate;
export type SRExtractionTemplate = z.infer<typeof SRExtractionTemplateSchema>;

// ─── Params ────────────────────────────────────────────────────────────
// These interfaces are hand-written because they represent frontend query
// parameters (pagination, filters) that are not part of the OpenAPI response schemas.
// They will be replaced by generated query param types once openapi-typescript
// supports request parameter extraction.

export interface SRListParams extends PaginationParams {
  workspaceId: string;
}

export interface SRRecordListParams extends PaginationParams {
  workspaceId: string;
  reviewId: string;
  status?: SRRecordStatus;
}

// ─── Response types ────────────────────────────────────────────────────

export type SRListResponse = PaginatedResponse<SRProjectListItem>;
export type SRRecordListResponse = PaginatedResponse<SRRecordListItem>;

// ─── Adapter Interface ─────────────────────────────────────────────────

export interface ISystematicReviewsAdapter {
  // Project CRUD
  list(params: SRListParams): Promise<SRListResponse>;
  detail(reviewId: string, workspaceId: string): Promise<SRProjectRead>;
  create(data: SRProjectCreate, workspaceId: string): Promise<SRProjectRead>;
  update(reviewId: string, data: SRProjectUpdate, workspaceId: string): Promise<SRProjectRead>;

  // Funnel
  getFunnel(reviewId: string, workspaceId: string): Promise<FunnelSummary>;

  // Screening
  getScreeningStats(
    reviewId: string,
    workspaceId: string,
    stage: SRScreeningStage,
  ): Promise<SRScreeningStats>;
  getExclusionAnalysis(
    reviewId: string,
    workspaceId: string,
    stage: SRScreeningStage,
  ): Promise<SRExclusionAnalysis>;

  // Records
  listRecords(params: SRRecordListParams): Promise<SRRecordListResponse>;
  getRecord(reviewId: string, recordId: string, workspaceId: string): Promise<SRRecordRead>;

  // Decisions
  submitDecision(
    reviewId: string,
    recordId: string,
    decision: SRDecision,
    stage: SRScreeningStage,
    exclusionReasonIds: string[],
    workspaceId: string,
  ): Promise<SRDecisionRead>;

  // Conflicts
  listConflicts(reviewId: string, workspaceId: string): Promise<SRConflictRead[]>;
  resolveConflict(
    reviewId: string,
    conflictId: string,
    body: SRConflictResolveConsensus,
    workspaceId: string,
  ): Promise<SRStageOutcomeRead>;

  // Members
  listMembers(reviewId: string, workspaceId: string): Promise<SRMemberRead[]>;
  addMember(reviewId: string, data: SRMemberAdd, workspaceId: string): Promise<SRMemberRead>;
  removeMember(reviewId: string, userId: string, workspaceId: string): Promise<void>;

  // Status
  advanceStatus(
    reviewId: string,
    data: SRAdvanceStatus,
    workspaceId: string,
  ): Promise<SRProjectRead>;

  // Protocol
  finalizeProtocol(reviewId: string, workspaceId: string): Promise<SRProjectRead>;

  // Criteria
  listCriteria(reviewId: string, workspaceId: string): Promise<SRCriterionRead[]>;
  createCriterion(
    reviewId: string,
    data: SRCriterionCreate,
    workspaceId: string,
  ): Promise<SRCriterionRead>;
  updateCriterion(
    reviewId: string,
    criterionId: string,
    data: SRCriterionUpdate,
    workspaceId: string,
  ): Promise<SRCriterionRead>;

  // Import
  importRecords(
    reviewId: string,
    file: File,
    sourceType: SRRecordSource,
    workspaceId: string,
  ): Promise<SRImportResult>;

  // Exclusion Reasons
  listExclusionReasons(reviewId: string, workspaceId: string): Promise<SRExclusionReasonRead[]>;
  createExclusionReason(
    reviewId: string,
    data: SRExclusionReasonCreate,
    workspaceId: string,
  ): Promise<SRExclusionReasonRead>;

  // Extraction Fields
  listExtractionFields(reviewId: string, workspaceId: string): Promise<SRExtractionFieldRead[]>;
  createExtractionField(
    reviewId: string,
    data: SRExtractionFieldCreate,
    workspaceId: string,
  ): Promise<SRExtractionFieldRead>;
  updateExtractionField(
    reviewId: string,
    fieldId: string,
    data: SRExtractionFieldUpdate,
    workspaceId: string,
  ): Promise<SRExtractionFieldRead>;

  // Extraction Values
  getRecordExtraction(
    reviewId: string,
    recordId: string,
    workspaceId: string,
  ): Promise<SRExtractionValueRead[]>;
  submitExtractionValue(
    reviewId: string,
    recordId: string,
    fieldId: string,
    data: SRExtractionValueCreate,
    workspaceId: string,
  ): Promise<SRExtractionValueRead>;
  completeExtraction(
    reviewId: string,
    recordId: string,
    workspaceId: string,
  ): Promise<SRExtractionRecordSummary>;

  // Risk of Bias
  listRoBAssessments(reviewId: string, workspaceId: string): Promise<SRRoBAssessmentRead[]>;
  createRoBAssessment(
    reviewId: string,
    data: SRRoBAssessmentCreate,
    workspaceId: string,
  ): Promise<SRRoBAssessmentRead>;
  getRoBAssessment(
    reviewId: string,
    assessmentId: string,
    workspaceId: string,
  ): Promise<SRRoBAssessmentRead>;
  updateRoBAssessment(
    reviewId: string,
    assessmentId: string,
    data: SRRoBAssessmentUpdate,
    workspaceId: string,
  ): Promise<SRRoBAssessmentRead>;
  completeRoBAssessment(
    reviewId: string,
    assessmentId: string,
    workspaceId: string,
  ): Promise<SRRoBAssessmentRead>;
  getRoBTrafficLight(reviewId: string, workspaceId: string): Promise<SRRoBTrafficLightEntry[]>;

  // Manuscript
  getManuscript(reviewId: string, workspaceId: string): Promise<SRManuscriptRead>;
  generateManuscript(reviewId: string, workspaceId: string): Promise<SRManuscriptGenerateResponse>;
  regenerateManuscript(
    reviewId: string,
    data: SRRegenerateRequest,
    workspaceId: string,
  ): Promise<SRRegenerateResponse>;
  getManuscriptStaleness(reviewId: string, workspaceId: string): Promise<SRSectionStaleness[]>;
  updateManuscriptSection(
    reviewId: string,
    sectionType: SRManuscriptSectionType,
    data: SRSectionLockUpdate,
    workspaceId: string,
  ): Promise<SRManuscriptSectionRead>;

  // PRISMA
  getPrismaCounts(reviewId: string, workspaceId: string): Promise<SRPrismaCounts>;
  getPrismaDiagram(reviewId: string, workspaceId: string): Promise<string>;

  // Evidence Table
  getEvidenceTable(reviewId: string, workspaceId: string): Promise<SREvidenceTable>;

  // Exports
  getExportUrl(reviewId: string, exportType: string, workspaceId: string): string;

  // Audit Trail
  getAuditTrail(
    reviewId: string,
    workspaceId: string,
    page?: number,
    pageSize?: number,
  ): Promise<SRAuditTrailPage>;

  // Amendments
  createAmendment(
    reviewId: string,
    data: SRAmendmentCreate,
    workspaceId: string,
  ): Promise<SRAmendmentRead>;

  // Extraction Templates
  listExtractionTemplates(reviewId: string, workspaceId: string): Promise<SRExtractionTemplate[]>;
  applyExtractionTemplate(
    reviewId: string,
    templateKey: string,
    workspaceId: string,
  ): Promise<SRExtractionFieldRead[]>;
}
